import React, { useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { Search, X } from 'react-bootstrap-icons';
import { Link, useSearchParams } from 'react-router-dom';

import { SortableTableHeader, useSortableTable } from '../../../../components/sortable-table-header';
import { SupportedCurrency } from '../../../../domain/currency';
import { ContractorStatsEntity, StatsEntity } from '../../../../domain/stats';
import { countAllOrders, formatOrderCount } from './ordersCount';

type ContractorsStatsForMonthProps = {
    stats: Pick<StatsEntity, 'contractorsStats' | 'contractorsStatsForAppliedCreator'>;
    forCurrency: SupportedCurrency;
}

type SortMode = 'orderCount' | 'totalProfit' | 'minProfit' | 'maxProfit' | 'averageProfit' | 'averageFreightValue';


export const ContractorsStatsForMonth = ({ stats, forCurrency }: ContractorsStatsForMonthProps) => {
    const [appliedCreatorView, setAppliedCreatorView] = useState(!!stats.contractorsStatsForAppliedCreator);

    useEffect(() => {
        setAppliedCreatorView(!!stats.contractorsStatsForAppliedCreator);
    }, [stats.contractorsStats, stats.contractorsStatsForAppliedCreator]);

    const [searchParams, setSearchParams] = useSearchParams();

    const { contractorsStats, contractorsStatsForAppliedCreator } = stats;

    // Group transport orders by contractor
    const { sortBy, sortDirection, handleSort, sortItems } = useSortableTable<SortMode>({
        initialSortBy: 'orderCount',
        initialDirection: 'desc',
        comparator: (a: ContractorStatsEntity, b: ContractorStatsEntity, sortKey, direction) => {
            const multiplier = direction === 'desc' ? 1 : -1;
            switch (sortKey) {
                case 'orderCount':
                    return multiplier * (countAllOrders(b.orderCount) - countAllOrders(a.orderCount));
                case 'minProfit':
                    return multiplier * (b.minProfit - a.minProfit);
                case 'maxProfit':
                    return multiplier * (b.maxProfit - a.maxProfit);
                case 'averageProfit':
                    return multiplier * (b.averageProfit - a.averageProfit);
                case 'totalProfit':
                    return multiplier * (b.totalProfit - a.totalProfit);
                case 'averageFreightValue':
                    return multiplier * (b.averageFreightValue - a.averageFreightValue);
                default:
                    return 0;
            }
        },
    });

    const sortedContractorsStats = sortItems(appliedCreatorView ? contractorsStatsForAppliedCreator?.stats || [] : contractorsStats);

    if (sortedContractorsStats.length === 0) {
        return (
            <>
                <h4>Podwykonawcy</h4>
                <p className="text-muted">Brak podwykonawców w wybranym okresie</p>
            </>
        );
    }

    const formatWithCurrency = (value: number) => value.toLocaleString('pl-PL', {
        style: 'currency',
        currency: forCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const handleContractorSearch = (contractorId: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('contractorId', contractorId);
        setSearchParams(searchParams);
    };

    const handleRemoveContractorFilter = () => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('contractorId');
        setSearchParams(searchParams);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Podwykonawcy ({sortedContractorsStats.length})</h4>
                {stats.contractorsStatsForAppliedCreator && (
                    <Form.Select
                        className="w-auto"
                        value={appliedCreatorView ? 'applied' : 'all'}
                        onChange={(e) => setAppliedCreatorView(e.target.value === 'applied')}
                    >
                        <option value="all">Wszyscy</option>
                        <option value="applied">Dla wybranego spedytora</option>
                    </Form.Select>
                )}
            </div>
            <Table striped bordered hover responsive>
                <thead>
                <tr>
                    <th rowSpan={2}></th>
                    <th rowSpan={2} className="text-center text-nowrap">Nazwa</th>
                    <SortableTableHeader
                        label="Liczba zleceń"
                        sortKey="orderCount"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        rowSpan={2}
                    />
                    <th
                        colSpan={4}
                        className="text-center text-nowrap"
                    >
                        Marża
                    </th>
                    <SortableTableHeader
                        label="Śr. wartość frachtu"
                        sortKey="averageFreightValue"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        rowSpan={2}
                    />
                </tr>
                <tr>
                    <SortableTableHeader
                        label="Min"
                        sortKey="minProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Maks"
                        sortKey="maxProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Średnia"
                        sortKey="averageProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Suma"
                        sortKey="totalProfit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                </tr>
                </thead>
                <tbody>
                {sortedContractorsStats.map((contractor) => (
                    <tr key={contractor.id}>
                        <td className="width-0 p-0 text-center align-middle">
                            <Button
                                className="m-0 p-0 px-1 d-flex align-items-center justify-content-center mx-auto"
                                variant="link"
                                onClick={() => handleContractorSearch(contractor.id)}
                            >
                                {searchParams.get('contractorId') === contractor.id ? (
                                    <X onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveContractorFilter();
                                    }} />
                                ) : (
                                    <Search />
                                )}
                            </Button>
                        </td>
                        <td className="width-250">
                            <Link
                                className={searchParams.get('contractorId') === contractor.id ? 'fw-bold' : ''}
                                to={`/contractors/${contractor.id}`}>
                                {contractor.name}
                            </Link>
                        </td>
                        <td className="text-center width-0 align-middle">{formatOrderCount(contractor.orderCount)}</td>
                        <td className={`text-center width-0 align-middle ${contractor.minProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(contractor.minProfit)}
                        </td>
                        <td className={`text-center width-0 align-middle ${contractor.maxProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(contractor.maxProfit)}
                        </td>
                        <td className={`text-center width-0 align-middle ${contractor.averageProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(contractor.averageProfit)}
                        </td>
                        <td className={`text-center width-0 align-middle ${contractor.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatWithCurrency(contractor.totalProfit)}
                        </td>
                        <td className="text-center width-0 align-middle">
                            {formatWithCurrency(contractor.averageFreightValue)}
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>
        </>
    );
};
